import Entity from '../../../../core/domain/entity';
import UniqueEntityID from '../../../../core/domain/unique_entity_id';
import { SharedDocumentId } from '../../../document/domain/shared_document/shared_document';
import { PatientId } from '../../../patient/domain/patient';
import { CreditId } from '../credit/credit';
import { DietitianId } from '../dietitian';
import { EstimateId } from '../estimate/estimate';
import { ThirdParty } from '../third_party/third-party';
import { BillLine } from './bill-line';

export type PaymentMethod = 'CB' | 'VIR' | 'CHQ' | 'ESP' | 'MSDP' | 'DLIB';

export enum BillStatus {
  DRAFT = 'DRAFT',
  EMITTED = 'EMITTED',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export interface BillProps {
  dietitianId: DietitianId;
  patientId: PatientId | undefined;
  thirdParty: ThirdParty | undefined;
  referencePrefix?: string | undefined;
  reference?: number | undefined;
  billedAt: Date | undefined;
  patientFullname: string | undefined;
  lines: BillLine[];
  amountExclTax: number;
  amountInclTax: number;
  amountTax: number;
  rateTax: number | undefined;
  withTax: boolean | undefined;
  draftedAt?: Date | undefined;
  emittedAt?: Date | undefined;
  paidAt?: Date | undefined;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;
  archived: boolean;
  archivedAt?: Date | undefined;
  status?: BillStatus | undefined;
  url?: string | undefined;
  path?: string | undefined;
  documentHasReady: boolean;
  paymentMethod: PaymentMethod | undefined;
  estimateId?: EstimateId | undefined;
  sharedDocumentId?: SharedDocumentId | undefined;
  sharedAt?: Date | undefined;
  creditId?: CreditId | undefined;
  eInvoiceStatus?: string | undefined;
  eInvoiceId?: string | undefined;
}

export class BillId extends Entity<unknown> {
  private constructor(id?: UniqueEntityID) {
    super(null, id);
  }

  public static create(id?: UniqueEntityID): BillId {
    return new BillId(id);
  }
}

export class Bill extends Entity<BillProps> {
  get billId(): BillId {
    return BillId.create(this.id);
  }

  get dietitianId(): DietitianId | undefined {
    return this.props.dietitianId ?? undefined;
  }

  get patientId(): PatientId | undefined {
    return this.props.patientId;
  }

  set patientId(value: PatientId | undefined) {
    this.props.patientId = value;
  }

  get thirdParty(): ThirdParty | undefined {
    return this.props.thirdParty;
  }

  set thirdParty(value: ThirdParty | undefined) {
    this.props.thirdParty = value;
  }

  get referencePrefix(): string | undefined {
    return this.props.referencePrefix;
  }

  set referencePrefix(value: string | undefined) {
    this.props.referencePrefix = value;
  }

  get reference(): number | undefined {
    return this.props.reference;
  }

  get billedAt(): Date | undefined {
    return this.props.billedAt;
  }

  set billedAt(value: Date | undefined) {
    this.props.billedAt = value;
  }

  get patientFullname(): string | undefined {
    return this.props.patientFullname;
  }

  set patientFullname(value: string | undefined) {
    this.props.patientFullname = value;
  }

  get lines(): BillLine[] {
    return this.props.lines;
  }

  get amountExclTax(): number {
    return this.props.amountExclTax;
  }

  get amountInclTax(): number {
    return this.props.amountInclTax;
  }

  get amountTax(): number {
    return this.props.amountTax;
  }

  get rateTax(): number | undefined {
    return this.props.rateTax;
  }

  get withTax(): boolean | undefined {
    return this.props.withTax;
  }

  set withTax(value: boolean | undefined) {
    this.props.withTax = value;
  }

  get draftedAt(): Date | undefined {
    return this.props.draftedAt;
  }

  set draftedAt(value: Date | undefined) {
    this.props.draftedAt = value;
  }

  get emittedAt(): Date | undefined {
    return this.props.emittedAt;
  }

  set emittedAt(value: Date | undefined) {
    this.props.emittedAt = value;
  }

  get paidAt(): Date | undefined {
    return this.props.paidAt;
  }

  set paidAt(value: Date | undefined) {
    this.props.paidAt = value;
  }

  get createdAt(): Date | undefined {
    return this.props.createdAt;
  }

  get updatedAt(): Date | undefined {
    return this.props.updatedAt;
  }

  get archived(): boolean {
    return this.props.archived;
  }

  get archivedAt(): Date | undefined {
    return this.props.archivedAt;
  }

  get status(): BillStatus | undefined {
    return this.props.status;
  }

  set status(value: BillStatus | undefined) {
    this.props.status = value;
  }

  get url(): string | undefined {
    return this.props.url;
  }

  set url(value: string | undefined) {
    this.props.url = value;
  }

  get path(): string | undefined {
    return this.props.path;
  }

  set path(value: string | undefined) {
    this.props.path = value;
  }

  get documentHasReady(): boolean {
    return this.props.documentHasReady;
  }

  set documentHasReady(value: boolean) {
    this.props.documentHasReady = value;
  }

  get paymentMethod(): PaymentMethod | undefined {
    return this.props.paymentMethod;
  }

  set paymentMethod(value: PaymentMethod | undefined) {
    this.props.paymentMethod = value;
  }

  get estimateId(): EstimateId | undefined {
    return this.props.estimateId;
  }

  get sharedDocumentId(): SharedDocumentId | undefined {
    return this.props.sharedDocumentId;
  }

  set sharedDocumentId(value: SharedDocumentId | undefined) {
    this.props.sharedDocumentId = value;
  }

  get sharedAt(): Date | undefined {
    return this.props.sharedAt;
  }

  set sharedAt(value: Date | undefined) {
    this.props.sharedAt = value;
  }

  get creditId(): CreditId | undefined {
    return this.props.creditId;
  }

  set creditId(value: CreditId | undefined) {
    this.props.creditId = value;
  }

  get eInvoiceStatus(): string | undefined {
    return this.props.eInvoiceStatus;
  }

  set eInvoiceStatus(value: string | undefined) {
    this.props.eInvoiceStatus = value;
  }

  get eInvoiceId(): string | undefined {
    return this.props.eInvoiceId;
  }

  set eInvoiceId(value: string | undefined) {
    this.props.eInvoiceId = value;
  }

  private constructor(props: BillProps, id?: UniqueEntityID) {
    super(props, id);
  }

  copyWith(props: BillProps): Bill {
    return Bill.create(
      {
        ...this.props,
        ...props,
      },
      this.id,
    );
  }

  archive(): Bill {
    return this.copyWith({ archivedAt: new Date() } as BillProps);
  }

  restore(): Bill {
    return this.copyWith({ archivedAt: undefined } as BillProps);
  }

  public static create(props: BillProps, id?: UniqueEntityID): Bill {
    return new Bill(props, id);
  }
}
